import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import OutboundIcon from '@mui/icons-material/OutboundOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { filterLastNMonths, groupCountBy, groupSumByMonth } from '@/lib/dashboardAggregations';

const TREND_MONTHS = 6;
import { usePortalCategoryColors } from '../portalColors';
import type { ClientPortalDashboardSummary } from '@/types/reports';
import type { GrnRegisterRow, DispatchOrderRegisterRow } from '@/types/reports';
import type { InvoiceDto } from '@/types/billing';

const STATUS_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3', '#ba1a1a', '#6b6f76'];
const formatCurrency = (v: number) => `₹${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function OverviewPanel() {
  const { t } = useTranslation('clientPortal');
  const colors = usePortalCategoryColors();

  const { data: summary } = useQuery({
    queryKey: ['client-portal', 'dashboard-summary'],
    queryFn: async () => (await apiClient.get<ClientPortalDashboardSummary>('/client-portal/dashboard-summary')).data,
  });
  const { data: grns = [], isLoading: loadingGrn } = useQuery({
    queryKey: ['client-portal', 'grn-register'],
    queryFn: async () => (await apiClient.get<GrnRegisterRow[]>('/client-portal/grn-register')).data,
  });
  const { data: dispatches = [], isLoading: loadingDispatch } = useQuery({
    queryKey: ['client-portal', 'dispatch-order-register'],
    queryFn: async () => (await apiClient.get<DispatchOrderRegisterRow[]>('/client-portal/dispatch-order-register')).data,
  });
  const { data: invoices = [], isLoading: loadingInvoices } = useQuery({
    queryKey: ['client-portal', 'invoices'],
    queryFn: async () => (await apiClient.get<InvoiceDto[]>('/client-portal/invoices')).data,
  });

  const cards = [
    { label: t('dashboardPage.stockValue'), value: formatCurrency(summary?.totalStockValue ?? 0), icon: <PaymentsIcon />, accentColor: colors.overview },
    { label: t('dashboardPage.pendingGrns'), value: summary?.pendingGrnCount ?? 0, icon: <MoveToInboxIcon />, accentColor: colors.grn },
    { label: t('dashboardPage.pendingDispatchOrders'), value: summary?.pendingDispatchOrderCount ?? 0, icon: <OutboundIcon />, accentColor: colors.dispatch },
    { label: t('dashboardPage.unpaidInvoices'), value: summary?.unpaidInvoiceCount ?? 0, icon: <ReceiptLongIcon />, accentColor: colors.invoices },
    { label: t('dashboardPage.unpaidInvoiceAmount'), value: formatCurrency(summary?.unpaidInvoiceAmount ?? 0), icon: <ReceiptLongIcon />, accentColor: colors.invoices },
  ];

  const monthlyActivity = useMemo(() => {
    const recentGrns = filterLastNMonths(grns, (r) => r.grnDate, TREND_MONTHS);
    const recentDispatches = filterLastNMonths(dispatches, (r) => r.orderDate, TREND_MONTHS);
    const grnByMonth = groupSumByMonth(recentGrns, (r) => r.grnDate, [{ key: 'count', value: () => 1 }]);
    const dispatchByMonth = groupSumByMonth(recentDispatches, (r) => r.orderDate, [{ key: 'count', value: () => 1 }]);
    const months = Array.from(new Set([...grnByMonth.map((m) => m.month), ...dispatchByMonth.map((m) => m.month)]));
    return {
      categories: months,
      grnCounts: months.map((m) => grnByMonth.find((x) => x.month === m)?.values.count ?? 0),
      dispatchCounts: months.map((m) => dispatchByMonth.find((x) => x.month === m)?.values.count ?? 0),
    };
  }, [grns, dispatches]);

  const invoiceStatusDistribution = useMemo(() => groupCountBy(invoices, (i) => i.status), [invoices]);
  const hasActivityData = monthlyActivity.grnCounts.some((v) => v > 0) || monthlyActivity.dispatchCounts.some((v) => v > 0);
  const hasInvoiceData = invoiceStatusDistribution.length > 0;

  return (
    <Stack spacing={3}>
      <GridContainer columns={10}>
        {cards.map((card) => (
          <GridItem key={card.label} xs={10} sm={5} md={2}>
            <StatCard {...card} />
          </GridItem>
        ))}
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard
            title={t('overviewPanel.activityTitle')}
            subtitle={t('overviewPanel.activitySubtitle')}
            loading={loadingGrn || loadingDispatch}
            empty={!loadingGrn && !loadingDispatch && !hasActivityData}
          >
            <DashboardBarChart
              categories={monthlyActivity.categories}
              series={[
                { label: t('grn'), data: monthlyActivity.grnCounts, color: colors.grn },
                { label: t('dispatchOrders'), data: monthlyActivity.dispatchCounts, color: colors.dispatch },
              ]}
              showValueLabels
              legendPosition="bottom"
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('overviewPanel.invoiceStatusTitle')} loading={loadingInvoices} empty={!loadingInvoices && !hasInvoiceData}>
            <DashboardPieChart
              data={invoiceStatusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))}
              legendPosition="bottom"
            />
          </ChartCard>
        </GridItem>
      </GridContainer>
    </Stack>
  );
}
